import React, { Suspense, useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { Bounds, Center, OrbitControls, useGLTF } from '@react-three/drei';
import axiosInstance from '../../services/axios.config';

// Reuse signed URLs across viewer openings.
// Backend URLs expire after 900 seconds; refresh them early.
const MODEL_URL_TTL_MS = 13 * 60 * 1000;

const modelUrlCache = new Map<
  string,
  { url: string; expiresAt: number }
>();

const pendingModelRequests = new Map<string, Promise<string>>();

async function getCachedModelUrl(modelType: string): Promise<string> {
  const cached = modelUrlCache.get(modelType);

  if (cached && Date.now() < cached.expiresAt) {
    return cached.url;
  }

  const pending = pendingModelRequests.get(modelType);
  if (pending) return pending;

  const request = axiosInstance
    .get(`/trailer-models/${encodeURIComponent(modelType)}`)
    .then((response) => {
      const url = response.data.url as string;

      modelUrlCache.set(modelType, {
        url,
        expiresAt: Date.now() + MODEL_URL_TTL_MS
      });

      return url;
    })
    .finally(() => {
      pendingModelRequests.delete(modelType);
    });

  pendingModelRequests.set(modelType, request);
  return request;
}

interface Trailer3DViewerProps {
  modelType?: string;
}

function TrailerModel({ url }: { url: string }) {
  const { scene } = useGLTF(url);

  return (
    <Bounds fit clip observe margin={1.25}>
      <Center>
        <primitive object={scene} />
      </Center>
    </Bounds>
  );
}

function LoadingModel() {
  return null;
}

export default function Trailer3DViewer({
  modelType = '53ft-dry-van'
}: Trailer3DViewerProps) {
  const [modelUrl, setModelUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setModelUrl(null);

    getCachedModelUrl(modelType)
      .then((url) => {
        if (!cancelled) {
          setModelUrl(url);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Unable to load trailer model.');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [modelType]);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        minHeight: 400,
        position: 'relative',
        background: '#e8edf2',
        borderRadius: 8,
        overflow: 'hidden'
      }}
    >
      {loading && (
        <div style={{ padding: 20 }}>
          Loading 3D trailer model...
        </div>
      )}

      {error && (
        <div style={{ padding: 20, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      {modelUrl && !error && (
        <Canvas
          camera={{ position: [15, 10, 20], fov: 45 }}
          gl={{ antialias: true }}
          style={{ width: '100%', height: '100%' }}
        >
          <ambientLight intensity={1.5} />
          <directionalLight position={[10, 20, 10]} intensity={2} />

          <Suspense fallback={<LoadingModel />}>
            <TrailerModel url={modelUrl} />
          </Suspense>

          <OrbitControls
            makeDefault
            enableRotate
            enableZoom
            enablePan
          />
        </Canvas>
      )}
    </div>
  );
}
